import { useCallback, useEffect, useRef, type RefObject } from 'react'

import { useReducedMotion } from '../../ui/motion'
import { throughSection, useScrollProgress } from './scroll'
import type { WorldHandle } from './world/engine'

/**
 * The pinned stage behind the landing page's story.
 *
 * It stays on screen while the story's section scrolls past it, and tells the
 * world engine how far through the section the reader is. The engine is its
 * own chunk, imported when the stage mounts, so the first bundle carries this
 * component and not the globe; until it arrives the stage is simply dark,
 * which is also what a failed import leaves behind.
 *
 * With reduced motion the story is still told by scrolling — each step is
 * drawn finished as the reader reaches it — so the stage keeps following the
 * scroll; only the engine's own clock is off.
 */
export function WorldStage({ section }: { section: RefObject<HTMLElement | null> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const engine = useRef<WorldHandle | null>(null)
  const progress = useRef(0)
  const reduced = useReducedMotion()

  useEffect(() => {
    const canvas = canvasRef.current
    // jsdom has no matchMedia and no canvas: tests get the stage without a picture.
    if (!canvas || typeof window.matchMedia !== 'function') return undefined
    let cancelled = false
    void import('./world/engine')
      .then(({ createWorld }) => {
        if (cancelled) return
        engine.current = createWorld(canvas, { reduced })
        engine.current.setProgress(progress.current)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
      engine.current?.destroy()
      engine.current = null
    }
  }, [reduced])

  const forward = useCallback((value: number) => {
    progress.current = value
    engine.current?.setProgress(value)
  }, [])
  useScrollProgress(section, throughSection, forward, { settled: 0, evenWhenReduced: true })

  return (
    <div className="world-stage" aria-hidden="true">
      <canvas ref={canvasRef} className="world-canvas" />
    </div>
  )
}
